import { useQuery } from '@apollo/client';
import {
  RECOMMENDATIONS_DATA_QUERY,
  useConsentFromDatabridge,
  CONSENT_IDENTIFIERS,
} from '@xp-utilities/web';
import {
  useState,
  useEffect,
  useContext,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import BreakpointContext from '../../../context/breakpoint';
import useDigitalData from '../../../hooks/useDigitalData';
import { AUTOSUGGEST_THRESHOLD, PLACEMENT_IDS } from '../../../tools/constants';
import parsePlacementIds from '../../../tools/parsePlacementIds';
import { DD_SEARCH_FLYOUT_ALGONOMY_SUGGESTIONS_TEST, DD_HAS_ALGONOMY_REFETCH_ON_CONSENT_INTERACTION_ENABLED } from '../../DigitalDataProvider';
import useFeatureFlags from './useFeatureFlags';

/**
 * Custom hook to compute the best suggestion from a search prefix and API response,
 * and trigger a recommendation query using Algonomy.
 *
 * @param {Array} apiResponse - Algonomy response object containing product suggestions.
 * @param {string} searchPrefix - User's current input in the search box.
 * @returns {{
*   searchTypePlacements: Array,
*   algonomyProductsLoading: boolean,
*   showRecommendations: boolean
* }}
*/
const useBestSuggestion = (apiResponse, searchPrefix) => {
  const [bestSuggestion, setBestSuggestion] = useState(searchPrefix);
  const [hasUserConsent, setHasUserConsent] = useState(null);
  const lastQueriedSuggestionRef = useRef(null);
  const lastApiResponseRef = useRef(null);

  const breakpoints = useContext(BreakpointContext);

  const resolvedPlacementIds = useMemo(() => {
    const placementIds = `${PLACEMENT_IDS.mobile.searchtypeahead}|${PLACEMENT_IDS.desktop.searchtypeahead}`;
    return parsePlacementIds(placementIds, breakpoints);
  }, [breakpoints]);

  const { searchFlyoutAlgonomySuggestions } = useFeatureFlags();

  const {
    [DD_SEARCH_FLYOUT_ALGONOMY_SUGGESTIONS_TEST]: hasAlgonomySuggestionsTestEnabled,
    [DD_HAS_ALGONOMY_REFETCH_ON_CONSENT_INTERACTION_ENABLED]: hasRefetchOnConsentInteractionEnabled,
  } = useDigitalData([
    DD_SEARCH_FLYOUT_ALGONOMY_SUGGESTIONS_TEST,
    DD_HAS_ALGONOMY_REFETCH_ON_CONSENT_INTERACTION_ENABLED,
  ]);

  useConsentFromDatabridge(CONSENT_IDENTIFIERS?.TARGETING_COOKIES, (consent) => {
    if (hasRefetchOnConsentInteractionEnabled) setHasUserConsent(consent);
    else setHasUserConsent((prev) => (typeof prev === 'boolean' ? prev : consent));
  });

  const isQueryAllowed = apiResponse.length > 0
      && searchFlyoutAlgonomySuggestions
      && hasAlgonomySuggestionsTestEnabled
      && bestSuggestion.trim() !== ''
      && typeof hasUserConsent === 'boolean';

  /**
  * Finds the best matching word from a word count map.
  *
  * @param {string} word - Word to match against.
  * @param {Object} wordCounts - Map of word frequencies.
  * @returns {string} - Best matching word or original word if no match.
  */
  const findBestMatch = useCallback((word, wordCounts) => {
    const matches = Array.from(wordCounts.keys())
      .filter((suggested) => suggested.startsWith(word))
      .sort((a, b) => wordCounts.get(b) - wordCounts.get(a));

    return matches[0] || word;
  }, []);

  const areArraysEqual = (arr1, arr2) => JSON.stringify(arr1) === JSON.stringify(arr2);

  useEffect(() => {
    const trimmedPrefix = searchPrefix.trim();

    if (!trimmedPrefix || trimmedPrefix.length < AUTOSUGGEST_THRESHOLD) {
      setBestSuggestion(trimmedPrefix);
      return;
    }

    if (areArraysEqual(apiResponse, lastApiResponseRef.current)) {
      return; // Avoid recomputing on unchanged API response
    }

    lastApiResponseRef.current = apiResponse;

    const wordCounts = new Map();
    const searchWords = trimmedPrefix.toLowerCase().split(/\s+/);

    apiResponse.forEach((item) => {
      const suggestedWords = item?.suggestedTerm?.toLowerCase().split(/\s+/) || [];

      suggestedWords.forEach((word) => {
        searchWords.forEach((searchWord) => {
          if (word.startsWith(searchWord)) {
            wordCounts.set(word, (wordCounts.get(word) || 0) + 1);
          }
        });
      });
    });

    const bestGuess = searchWords.map((word) => findBestMatch(word, wordCounts)).join(' ');
    if (bestGuess !== bestSuggestion) {
      setBestSuggestion(bestGuess.trim());
    }
  }, [searchPrefix, apiResponse, findBestMatch, bestSuggestion]);

  /**
  * Prevents firing the query if the suggestion has not changed or isn't valid.
  */
  const shouldSkipQuery = !isQueryAllowed || bestSuggestion === lastQueriedSuggestionRef.current;

  const {
    data,
    loading: algonomyProductsLoading,
    previousData,
  } = useQuery(RECOMMENDATIONS_DATA_QUERY, {
    variables: {
      searchTerm: bestSuggestion,
      hasUserConsent,
      placements: resolvedPlacementIds,
    },
    skip: shouldSkipQuery,
  });

  const activeData = shouldSkipQuery && bestSuggestion === lastQueriedSuggestionRef.current
    ? previousData || data
    : data;

  useEffect(() => {
    if (activeData) {
      lastQueriedSuggestionRef.current = bestSuggestion;
    }
  }, [bestSuggestion, activeData]);

  return {
    searchTypePlacements: activeData?.recommendations?.placements || [],
    algonomyProductsLoading,
    showRecommendations: (activeData?.recommendations?.placements?.[0]?.products?.length ?? 0) > 0,
  };
};

export default useBestSuggestion;
